/**
 * Listed / Proposed / Finalized deal money display helpers.
 */

export function finiteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function formatDealMoney(value) {
  const n = finiteNumber(value, NaN);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
}

/**
 * Compact cell: show Finalized, and Listed above it when different.
 * Optional Proposed in the middle when present and different from both.
 */
export function formatPricingCompareCell({ listed, proposed, finalized } = {}) {
  const listedN = listed == null ? null : Math.round(finiteNumber(listed));
  const proposedN = proposed == null ? null : Math.round(finiteNumber(proposed));
  const finalizedN = finalized == null ? null : Math.round(finiteNumber(finalized));

  if (finalizedN == null && listedN == null && proposedN == null) return '—';

  const lines = [];
  if (listedN != null && listedN !== finalizedN) {
    lines.push({ label: 'Listed', value: formatDealMoney(listedN) });
  }
  if (
    proposedN != null &&
    proposedN !== finalizedN &&
    proposedN !== listedN
  ) {
    lines.push({ label: 'Proposed', value: formatDealMoney(proposedN) });
  }
  lines.push({
    label: lines.length ? 'Final' : null,
    value: formatDealMoney(finalizedN != null ? finalizedN : listedN ?? proposedN)
  });
  return lines;
}

export function getDealPricingCompare(deal, underConsideration = null) {
  if (deal?.pricing_compare) {
    if (!deal.pricing_compare.proposed && underConsideration) {
      return {
        ...deal.pricing_compare,
        proposed: {
          unit_price: finiteNumber(underConsideration.customer_proposal),
          plc_price: finiteNumber(underConsideration.plc_price),
          gst: finiteNumber(underConsideration.gst),
          extra_charges: finiteNumber(underConsideration.extra_charges),
          total: finiteNumber(underConsideration.total_price)
        }
      };
    }
    return deal.pricing_compare;
  }
  const listedUnit = finiteNumber(deal?.unit?.cost);
  const finalizedUnit = finiteNumber(deal?.unit_price);
  const discount = finiteNumber(deal?.discount);
  const listedFromDiscount =
    discount !== 0 ? finalizedUnit + discount : listedUnit || finalizedUnit;
  const listedPlc = finiteNumber(deal?.unit?.plc_price ?? deal?.plc_price);
  const listedGst = finiteNumber(deal?.unit?.gst ?? deal?.gst_price);
  const listedExtra = finiteNumber(deal?.unit?.extra_charges ?? deal?.extra_charges);
  const proposed = underConsideration
    ? {
        unit_price: finiteNumber(underConsideration.customer_proposal),
        plc_price: finiteNumber(underConsideration.plc_price),
        gst: finiteNumber(underConsideration.gst),
        extra_charges: finiteNumber(underConsideration.extra_charges),
        total: finiteNumber(underConsideration.total_price)
      }
    : null;
  const finalized = {
    unit_price: finalizedUnit,
    plc_price: finiteNumber(deal?.plc_price),
    gst: finiteNumber(deal?.gst_price),
    extra_charges: finiteNumber(deal?.extra_charges),
    total: finiteNumber(deal?.total_price)
  };
  const listed = {
    unit_price: listedFromDiscount,
    plc_price: listedPlc,
    gst: listedGst,
    extra_charges: listedExtra,
    total: listedFromDiscount + listedPlc + listedGst + listedExtra
  };
  return {
    listed,
    proposed,
    finalized,
    unit_discount: Math.round(listed.unit_price - finalized.unit_price),
    total_discount: Math.round(listed.total - finalized.total)
  };
}
